import React, { useEffect, useState } from "react";
import "./UpgradeLicensePopup.scss";
import { SetValue } from "../../../hooks/useLocalStorage";
import { useNavigate } from "react-router-dom";
import { CustomerTrialDetail } from "../../../types/customer/CustomerTrialDetail";
import useSwal from "../../../hooks/useSwal";
import useAuth from "../../../hooks/useAuth";
import { AccessPermissionEnum } from "../../../enums";
import { SweetAlertOptions } from "sweetalert2";

interface UpgradeLicensePopupProps{
    isOpen:boolean;
    setIsOpen: SetValue<boolean>;
    isClosing:boolean;
    setIsClosing:React.Dispatch<React.SetStateAction<boolean>>;
    daysLeftForTrial:number;
    setIsLicenseUpgradeButtonSessionState:SetValue<boolean>;
    fetchLabelKeyTranslation:(key: string, defaultValue: string) => string;
}

const SLIDE_DURATION = 280;

const UpgradeLicensePopup: React.FC<UpgradeLicensePopupProps> = ({
    isOpen,
    setIsOpen,
    isClosing,
    setIsClosing,
    daysLeftForTrial,
    setIsLicenseUpgradeButtonSessionState,
    fetchLabelKeyTranslation
}) => {
    const navigate = useNavigate();
    const swal = useSwal();
    const auth = useAuth();
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && isOpen) {
                dismiss();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    const dismiss = () => {
        if (isClosing) return;
        setIsClosing(true);
        setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
        }, SLIDE_DURATION);
    };

    const handleUpgrade = () => {
        if(!auth?.checkUserAccess(AccessPermissionEnum.ManageSecurityControl))
                    {
                        const swalOptions: SweetAlertOptions = {
                        icon: "info",
                        title: fetchLabelKeyTranslation("EnableAiErrTxt","Please contact your system administrator to enable advanced AI capabilities for all recorded users"),
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: fetchLabelKeyTranslation("OkTxt","O.K"),
                    };
                     swal.fire(swalOptions);
                    }
                    else
                    {
        navigate("/upgrade-licenses");
                    }
    };

    const remindMeLaterHandler=()=>{
        setIsLicenseUpgradeButtonSessionState(false);
        setIsOpen(false);
    }

    if(daysLeftForTrial === 0) return <></>;

    return (
        <div
            className={`upgrade-popover ${isClosing ? "closing" : "open"}`}
            role="dialog"
            aria-modal="false"
            aria-label="Copilot free trial popover"
        >
            <button
                aria-label="Close popover"
                className="upgrade-close"
                onClick={dismiss}
            >
                ×
            </button>

            <h4 className="text-center">
                {daysLeftForTrial===-1 ? fetchLabelKeyTranslation("AdvanceAiEnjoyTxt","We hope you enjoyed the advanced AI capabilities") : fetchLabelKeyTranslation("AdvanceAiUnlockedTxt","Advanced AI capabilities are unlocked!")}
            </h4>
            <h6 className="text-muted text-center">
                {daysLeftForTrial!==-1 && `${daysLeftForTrial} ${fetchLabelKeyTranslation("DayRemainingTxt","day(s) remaining.")}`}
            </h6>

            <p className="text-muted text-center">
                {daysLeftForTrial===-1 ? (
                    <>
                    {fetchLabelKeyTranslation("UpgradeNowTxtOne","These capabilities continue to be available for Ultimate IQ recorded users.")}
                    <br/>
                    {fetchLabelKeyTranslation("UpgradeNowTxtTwo","To continue for all recorded users upgrade now")}
                    </>
                ) : (
                    <>
                        {fetchLabelKeyTranslation("RealTimeAlertsTxtOne","Define your own topics and trigger real time alerts to act faster. Create scorecards to automatically evaluate conversation performance. Use CIQpilot to unlock deeper insight by asking questions to get evidence-based answers.")}
                        <br />
                        {fetchLabelKeyTranslation("RealTimeAlertsTxtTwo","To retain advanced AI capabilities")}
                    </>
                )}
            </p>


            <div className="upgrade-actions">
                <button className="btn-upgrade btn-ghost" onClick={remindMeLaterHandler}>
                    {fetchLabelKeyTranslation("RemindLaterTxt","Remind me later")}
                </button>
                <button className="btn-upgrade btn-primary" onClick={handleUpgrade}>
                    {daysLeftForTrial===-1 ? fetchLabelKeyTranslation("UpgradeTxt","Upgrade") : fetchLabelKeyTranslation("ContinueTxt","Continue")}
                </button>
            </div>
        </div>
    );
};

export default UpgradeLicensePopup;
